import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'store/store';
import {
  AppState,
  SignalingDisconnectPayload,
  SignalingInitData,
  SupportedLanguages,
} from 'features/application/types';
import { roomError, sessionDuplicationDetected, sessionIntercepted } from 'features/room/roomSlice';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import {
  pong,
  signalingConnected,
  signalingDisconnected,
  systemUpdate,
} from 'features/application/actions';
import {
  participantsLimitReached,
  sessionEnded,
  sessionLeft,
  signalingRoomJoined,
} from 'features/room/actions';
import { selectLocalUserId, selectUserEntries } from 'features/users/usersSlice';
import { streamingConnectionEstablished } from 'features/streaming/actions';
import {
  signalingAccessRejected,
  signalingUserBanned,
  signalingUserKicked,
} from 'features/users/actions';
import { userAgentDetails } from 'utils/userAgentDetails';
import { safeLocalStorage } from 'utils/storage';
import { isRelayRequiredRemembered } from 'utils/webrtc/relayRequiredStorage';
import { iceServerListProblem } from 'utils/webrtc/turnCredentials';

export const STORM_THRESHOLD = 3;

// Once the signaling connection has held this long without a new reconnect, the
// accumulated consecutiveCycles are treated as stale and cleared (see
// resetReconnectCyclesSaga). This decays the counter into a "recent density"
// signal so well-separated blips don't sum into a false storm, and lets the
// storm overlay clear after the network genuinely recovers.
export const STORM_STABILITY_WINDOW_MS = 10_000;

export const initialState: AppState = {
  signaling: {
    url: '',
    token: '',
    isReconnecting: false,
    isConnectionEstablished: false,
    iceServers: null,
    mediaServers: null,
    iceRelay: false,
    iceRelayFallback: false,
    relayRequired: false,
    dtlsStallRecovery: false,
    consecutiveCycles: 0,
  },
  streaming: {
    isReconnecting: false,
    isConnectionEstablished: false,
  },
  connectionMessageEnabled: true,
  connectionQualityIndicatorEnabled: false,
  isGlobalAudioMuted: false,
  audioOnlyMode: false,
  waitingScreen: null,
  deviceInfo: userAgentDetails(),
  language: 'en',
  supportedLanguages: [],
  isPiP: false,
};

export const applicationSlice = createSlice({
  name: 'application',
  initialState,
  reducers: {
    mediaServerError(state) {
      state.streaming.isReconnecting = true;
    },
    signalingReconnectionStarted(state) {
      // Dedup gate: keeps consecutiveCycles monotonic per logical cycle so
      // SignalingSocket.onClose can use `=== STORM_THRESHOLD` (fires once).
      if (!state.signaling.isReconnecting) {
        state.signaling.consecutiveCycles += 1;
      }
      state.signaling.isReconnecting = true;
    },
    signalingStabilized(state) {
      state.signaling.consecutiveCycles = 0;
    },
    supportedLanguagesLoaded(state, action: PayloadAction<SupportedLanguages>) {
      state.supportedLanguages = action.payload;
    },
    PiPToggled(state, action: PayloadAction<boolean>) {
      state.isPiP = action.payload;
    },
    globalAudioMuteToggled(state) {
      state.isGlobalAudioMuted = !state.isGlobalAudioMuted;
    },
    audioOnlyModeChanged(state, action: PayloadAction<boolean>) {
      state.audioOnlyMode = action.payload;
    },
    languageChanged(state, action: PayloadAction<string>) {
      state.language = action.payload;
    },
    // Set only by a never-established DTLS stall; a relay-restart success never sets it.
    iceRelayRequired(state) {
      state.signaling.relayRequired = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signalingConnected, (state) => {
        state.signaling.isConnectionEstablished = true;
        state.signaling.isReconnecting = false;
      })
      .addCase(
        signalingDisconnected,
        (state, action: PayloadAction<SignalingDisconnectPayload>) => {
          state.signaling.isConnectionEstablished = false;
          state.signaling.isReconnecting = false;
          state.signaling.consecutiveCycles = 0;

          if (action.payload.error) {
            state.waitingScreen = 'connectionFailure';
          }
        }
      )
      .addCase(streamingConnectionEstablished, (state) => {
        state.streaming.isConnectionEstablished = true;
        state.streaming.isReconnecting = false;
      })
      .addCase(pong, (state, action) => {
        state.signaling.token = action.payload.token;

        if (state.signaling.mediaServers?.publishing && action.payload.publishingMediaToken) {
          state.signaling.mediaServers.publishing.token = action.payload.publishingMediaToken;
        }

        if (state.signaling.mediaServers?.subscribing && action.payload.subscribingMediaToken) {
          state.signaling.mediaServers.subscribing.token = action.payload.subscribingMediaToken;
        }

        if (iceServerListProblem(action.payload.iceServers) === null) {
          state.signaling.iceServers = action.payload.iceServers;
        }
      })
      .addCase(authorizeUser.fulfilled, (state, action) => {
        state.signaling.token = action.payload.signallingToken;
        state.signaling.url = action.payload.signallingUrl;
        state.connectionMessageEnabled = action.payload.settings.connectionMessageEnabled;
        state.connectionQualityIndicatorEnabled =
          action.payload.settings.connectionQualityIndicatorEnabled;
      })
      .addCase(systemUpdate, (state, action) => {
        state.signaling.token = action.payload.token;
      })
      .addCase(signalingRoomJoined, (state, action) => {
        state.signaling.token = action.payload.token;
        state.signaling.iceServers = action.payload.iceServers;
        state.signaling.mediaServers = action.payload.mediaServers;
        state.signaling.iceRelay = action.payload.iceRelay;
        state.signaling.iceRelayFallback = action.payload.iceRelayFallback ?? false;
        state.signaling.dtlsStallRecovery = action.payload.dtlsStallRecovery ?? false;
      })
      .addCase(sessionLeft, (state) => {
        state.waitingScreen = 'sessionLeft';
        state.signaling.consecutiveCycles = 0;
      })
      .addCase(participantsLimitReached, (state) => {
        state.waitingScreen = 'participantsLimit';
      })
      .addCase(signalingUserKicked, (state, action) => {
        if (action.payload.reason === 'session_duplicated') {
          state.waitingScreen = 'sessionDuplicated';
        } else {
          state.waitingScreen = 'userKicked';
        }
      })
      .addCase(signalingUserBanned, (state, action) => {
        state.waitingScreen = 'userBanned';
        state.banDetails = action.payload;
      })
      .addCase(signalingAccessRejected, (state) => {
        state.waitingScreen = 'accessRejected';
      })
      .addCase(sessionDuplicationDetected, (state) => {
        state.waitingScreen = 'interceptSession';
      })
      .addCase(sessionIntercepted, (state) => {
        state.waitingScreen = null;
      })
      .addCase(sessionEnded, (state, action) => {
        state.waitingScreen = 'sessionEnded';
        state.endSessionInitiator = action.payload.initiator;
      })
      .addCase(roomError, (state, action) => {
        if (action.payload?.global) {
          state.waitingScreen = 'roomError';
        }
      });
  },
});

export const {
  PiPToggled,
  languageChanged,
  supportedLanguagesLoaded,
  mediaServerError,
  signalingReconnectionStarted,
  signalingStabilized,
  globalAudioMuteToggled,
  audioOnlyModeChanged,
  iceRelayRequired,
} = applicationSlice.actions;

export const selectWaitingScreen = (state: RootState) => state.application.waitingScreen;
export const selectSignalingInitData = (state: RootState): SignalingInitData => ({
  url: state.application.signaling.url,
  token: state.application.signaling.token,
  iceServers: state.application.signaling.iceServers,
  mediaServers: state.application.signaling.mediaServers,
  iceRelay: state.application.signaling.iceRelay,
});

export const selectAppReconnecting = (state: RootState) =>
  state.application.signaling.isReconnecting || state.application.streaming.isReconnecting;

export const selectConsecutiveReconnectCycles = (state: RootState) =>
  state.application.signaling.consecutiveCycles;

export const selectIsReconnectStorm = (state: RootState) =>
  state.application.signaling.consecutiveCycles >= STORM_THRESHOLD;

export const selectShouldShowReconnectOverlay = (state: RootState) =>
  selectAppReconnecting(state) || selectIsReconnectStorm(state);

export const selectIceRelay = (state: RootState) => state.application.signaling.iceRelay;
export const selectIceServers = (state: RootState): RTCIceServer[] | null =>
  state.application.signaling.iceServers;

// Debug lever mirrors `debug:forceIceRelay`: lets QA enable the fallback for one client.
export const selectIceRelayFallback = (state: RootState) =>
  state.application.signaling.iceRelayFallback ||
  safeLocalStorage.getItem('debug:forceIceRelayFallback') === '1';

export const selectIceRelayRequired = (state: RootState) =>
  state.application.signaling.relayRequired || isRelayRequiredRemembered();

// Debug lever mirrors `debug:forceIceRelayFallback`: lets QA enable the rebuild for one client.
export const selectDtlsStallRecovery = (state: RootState) =>
  state.application.signaling.dtlsStallRecovery ||
  safeLocalStorage.getItem('debug:forceDtlsStallRecovery') === '1';

export const selectIsPiP = (state: RootState) => state.application.isPiP;

export const selectIsGlobalAudioMuted = (state: RootState) => state.application.isGlobalAudioMuted;

export const selectIsAudioOnlyMode = (state: RootState) => state.application.audioOnlyMode;

export const selectDeviceInfo = (state: RootState) => state.application.deviceInfo;

// UA parser returns undefined for desktop clients and a specifier for everything else, hence default to desktop;
export const selectDeviceType = (state: RootState) =>
  state.application.deviceInfo.device.type || 'desktop';

export const selectEndSessionData = createSelector(
  [
    selectLocalUserId,
    selectUserEntries,
    (state: RootState) => state.application.endSessionInitiator,
  ],
  (localUserId, userEntries, endSessionInitiator) => {
    if (!endSessionInitiator?.id) {
      return {
        initiatorName: null,
        endedByMe: false,
      };
    }

    const initiatorUser = userEntries[endSessionInitiator.id];

    return {
      initiatorName: initiatorUser?.name || endSessionInitiator.name,
      endedByMe: localUserId === endSessionInitiator.id,
    };
  }
);

export const selectLanguage = (state: RootState) => state.application.language;

export const selectBanDetails = (state: RootState) => state.application.banDetails;

export const selectSupportedLanguages = (state: RootState) => state.application.supportedLanguages;

export const selectConnectionMessageEnabled = (state: RootState) =>
  state.application.connectionMessageEnabled;

export const selectConnectionQualityIndicatorEnabled = (state: RootState) =>
  state.application.connectionQualityIndicatorEnabled;

export default applicationSlice.reducer;
